/**
 * InlineDdlTypeBody — the embedded content for the `CREATE TYPE` /
 * `CREATE DOMAIN` editor, mounted directly (no trigger, no Popover chrome)
 * for the Compact VQE popover's `compactDdlBodyArm`. Keeps the
 * `objectKind: 'type' | 'domain'` prop that drives the testid suffix.
 *
 * Seeds the form from the grammar-backed DSE2 `extractParkedType` helper
 * (VDE-2 Slice 2) — the regex-free CREATE TYPE / CREATE DOMAIN extractor arm.
 *
 * Engine gating: mysql + sqlite have no user-defined type system — renders a
 * muted one-line notice pointing at column-level ENUM / CHECK constraints.
 * mssql + pgsql + oracle mount the form which dispatches via
 * `commitCreateType`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D4 — Type / Domain UI.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T4a/T5).
 */
import { useMemo } from 'react';

import type { QualifiedName } from '../../../sql-grammar/model';
import {
  getCreateTypeUnsupportedTooltip,
  isCreateTypeSupported,
} from '../edit/edits/createType';
import type { DdlObjectKind, VqeDdlEditContext } from '../edit/editContext';
import { TypeEditFormBody } from '../render/common/TypeEditFormBody';
import {
  defaultKindForEngine,
  type TypeFormValues,
} from '../render/common/typeFormHelpers';
import {
  extractParkedType,
  type ParsedParkedType,
} from '../../../sql-resolver/ddl/extract/type';

const FALLBACK_TYPE_NAME: QualifiedName = {
  name: '',
  raw: '',
  quoted: false,
  foldedName: '',
};

function valuesFromParsed(
  parsed: ParsedParkedType,
  fallbackKindForEngine: ReturnType<typeof defaultKindForEngine>,
): TypeFormValues {
  return {
    name: parsed.name.name,
    kind: parsed.kind ?? fallbackKindForEngine,
    enumValues: parsed.enumValues ? parsed.enumValues.join(', ') : '',
    attributes: parsed.attributes
      ? parsed.attributes.map((a) => `${a.name} ${a.typeRef}`).join('\n')
      : '',
    baseType: parsed.baseType ?? '',
    subtype: parsed.subtype ?? '',
    varraySize: parsed.varraySize !== undefined ? String(parsed.varraySize) : '',
    orReplace: parsed.orReplace,
    notNull: false,
    defaultText: '',
    checkExpressions: '',
  };
}

function useParsedParkedType(
  editContext: VqeDdlEditContext,
  parsedOverride: ParsedParkedType | null | undefined,
): ParsedParkedType | null {
  return useMemo<ParsedParkedType | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      return extractParkedType(buf, stmt);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);
}

export interface InlineDdlTypeBodyProps {
  readonly editContext: VqeDdlEditContext;
  /**
   * The cursor's classified objectKind — `'type'` for CREATE TYPE / CREATE
   * OR REPLACE TYPE, `'domain'` for CREATE DOMAIN. Drives the form testid
   * suffix and the unsupported-notice testid.
   */
  readonly objectKind: Extract<DdlObjectKind, 'type' | 'domain'>;
  /** Test seam — overrides the parsed parked-type (skips the scan). */
  readonly parsedOverride?: ParsedParkedType | null;
  /** Called after a successful commit (or Cancel) so the host can react. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

/**
 * Embedded body for the Compact VQE popover's `compactDdlBodyArm`, mounted
 * directly with no trigger/Popover chrome.
 */
export function InlineDdlTypeBody({
  editContext,
  objectKind,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlTypeBodyProps) {
  const engine = editContext.service.engineId;
  const parsed = useParsedParkedType(editContext, parsedOverride);

  const typeName = parsed?.name ?? FALLBACK_TYPE_NAME;
  const initialValues = parsed
    ? valuesFromParsed(parsed, defaultKindForEngine(engine))
    : undefined;

  if (!isCreateTypeSupported(engine)) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid={`inline-ddl-${objectKind}-unsupported`}
      >
        {getCreateTypeUnsupportedTooltip(engine)}
      </p>
    );
  }

  return (
    <TypeEditFormBody
      editContext={editContext}
      typeName={typeName}
      initialValues={initialValues}
      testId={`inline-ddl-${objectKind}-form`}
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
